<template>
	<!-- https://www.primefaces.org/primevue/showcase/#/dialog -->
	<!--
	-- some options are explicitly set here because their
	-- behavior is overridden by custom implementation, e.g.,
	-- close-on-escape, which relies on element.which (deprecated)
	-- and does not seem to work correctly
	-->
	<Dialog :class="classes"
		:visible="showDialog"
		:close-on-escape="false"
		:closable="false"
		:show-header="!!( $slots.header || $slots.heading )"
		:show-footer="!!$slots.footer"
	>
		<template #header>
			<!-- use header slot to replace *full* header -->
			<slot name="header">
				<div class="teaming-dialog__header">
					<h1 class="teaming-dialog__heading">
						<!-- use heading slot to replace *header text* only -->
						<slot name="heading"></slot>
					</h1>
					<FontAwesomeIcon
						v-if="closable"
						class="teaming-dialog__close-button"
						:icon="[ `fas`, `times` ]"
						@click.stop="$emit( `hide` )"
					/>
				</div>
			</slot>
		</template>

		<template #footer v-if="$slots.footer">
			<div class="teaming-dialog__footer">
				<slot name="footer"></slot>
			</div>
		</template>

		<template #default>
			<div class="teaming-dialog__content">
				<slot></slot>
			</div>
		</template>
	</Dialog>
</template>

<script>
import Dialog from "primevue/dialog";

import EventsMixin from "../mixins/EventsMixin";

export default {
	name: "TeamingDialog",
	mixins: [ EventsMixin, ],
	emits: [ "show", "hide", ],
	components: {
		Dialog,
	},
	props: {
		className: {
			type: String,
			default: "",
		},
		showDialog: {
			type: Boolean,
			required: true,
		},
		hideOnEscape: {
			type: Boolean,
			default: true,
		},
		closable: {
			type: Boolean,
			default: true,
		},
	},
	data() {
		return {
			escKeyHandler: null,
		};
	},
	computed: {
		classes() {
			return `teaming-dialog ${ this.className }`.trim();
		},
	},
	methods: {
		createKeyDownHandler() {
			this.disposeKeyDownHandler();
			this.escKeyHandler = e => {
				if ( e.key === "Escape" && this.hideOnEscape ) {
					e.preventDefault();
					e.stopPropagation();
					this.$emit( "hide" );
				}
			};
			document.addEventListener( "keydown", this.escKeyHandler );
		},
		disposeKeyDownHandler() {
			if ( this.escKeyHandler ) {
				document.removeEventListener( "keydown", this.escKeyHandler );
				this.escKeyHandler = null;
			}
		},
	},
	mounted() {
		this.createKeyDownHandler();
	},
	unmounted() {
		this.disposeKeyDownHandler();
	},
};
</script>

<style lang="scss" scoped>
.teaming-dialog__header,
.teaming-dialog__footer,
.teaming-dialog__content {
	display: flex;
	height: 100%;
	width: 100%;
}

.teaming-dialog__header {
	justify-content: space-between;
	align-items: flex-start;
}

.teaming-dialog__heading {
	@include font-and-line-for(2);
	font-weight: 600;
	color: $teaming-dialog-heading-color;
}

.teaming-dialog__close-button {
	color: $dialog-default-icon-color;
	cursor: pointer;

	&:hover {
		color: $dialog-default-icon-color--hover;
	}
}

.teaming-dialog__content {
	justify-content: flex-start;
}
</style>

<style lang="scss">
.teaming-dialog.p-dialog {
	max-width: 95vw;
	min-width: 50vw;

	@include for-size('xs') {
		max-width: 100vw;
		min-width: 90vw;
	}

	@include for-size('sm') {
		max-width: 85vw;
		min-width: 70vw;
	}

	@include for-size('md') {
		max-width: 75vw;
		min-width: 50vw;
	}

	@include for-size('lg') {
		max-width: 55vw;
		min-width: 30vw;
	}
}
</style>
